/**
 * Getting back in when the password is gone.
 *
 * Two screens, because it is two moments separated by a mailbox: asking for a
 * link, and spending one. They live in one file because the second is useless
 * without the first and the copy has to agree between them.
 *
 * THE THING THAT SHAPES BOTH: the server will not say whether an address has an
 * account, because an endpoint that does is a directory of who has registered
 * here. So the screen cannot say "no account with that address" either — it has
 * to be honest that it does not know, and useful anyway. The wording below does
 * that by naming the thing a person can actually check: what they typed.
 */
import React from "react";
import { api } from "./api.jsx";
import { useApp } from "./store.jsx";

const V = window.CardboardmarketDesignSystem_2a3239;

/** Step one: ask for the link. */
export function ForgotScreen() {
  const { go, trader, t, notify } = useApp();

  /*
   * Where "back to sign in" actually goes.
   *
   * Onboarding refuses anybody who is already set up — it calls `back()` on
   * mount, which returns them to the screen they just left. So sending a
   * SIGNED-IN person there is a button that appears to do nothing, which is
   * exactly how the same mistake looked on the reset screen. Somebody who is
   * already signed in has nothing to sign in to; browse is where they wanted to
   * be.
   */
  const leave = () => go(trader ? 'browse' : 'onboarding');
  const [email, setEmail] = React.useState('');
  const [sending, setSending] = React.useState(false);
  const [sent, setSent] = React.useState(null);   // null | { minutes }

  const submit = async (e) => {
    e.preventDefault();
    if (sending || !email.includes('@')) return;
    setSending(true);
    try {
      const r = await api.forgotPassword(email.trim());
      /*
       * The minutes come from the server rather than a number typed here. The
       * screen saying "an hour" while the token lasts fifteen minutes is the
       * kind of drift nobody notices until somebody is locked out and cross.
       */
      setSent({ minutes: (r && r.expiresInMinutes) || 60 });
    } catch (err) {
      notify(err.message, 'caution');
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <V.EmptyState
        icon="mail"
        title={t("Check your email")}
        description={t("If {email} has an account, a link to choose a new password is on its way. It lasts {minutes} minutes and works once.", { email: email.trim(), minutes: sent.minutes })}
        action={<V.Button onClick={leave}>{t("Back to sign in")}</V.Button>}
      />
    );
  }

  return (
    <div style={{ maxWidth: '26rem', margin: '0 auto', padding: 'var(--space-6) var(--space-4)' }}>
      {/*
        A plain h2 with the design system's own class, which is what every other
        screen in this app uses. There is no `Heading` component — reaching for
        one rendered `undefined`, and React answers an undefined element type by
        throwing, which blanks the whole page rather than dropping a heading.
      */}
      <h2 className="cbm-h2">{t("Forgot your password?")}</h2>
      <p style={{ font: 'var(--type-body)', color: 'var(--text-secondary)', marginTop: 'var(--space-2)' }}>
        {t("Tell us the email you signed up with and we will send you a link to choose a new password.")}
      </p>
      {/*
        A real form, so that Enter submits and a password manager understands
        which account this is about — the same reasoning as the sign-in form.
      */}
      <form onSubmit={submit} style={{ display: 'grid', gap: 'var(--space-4)', marginTop: 'var(--space-5)' }}>
        <V.Input
          label={t("Email")} type="email" autoComplete="email"
          value={email} onChange={(e) => setEmail(e.target.value)}
        />
        <V.Button type="submit" disabled={sending || !email.includes('@')}>
          {sending ? t("Sending…") : t("Send me a link")}
        </V.Button>
        <button
          type="button"
          onClick={leave}
          style={{ background: 'none', border: 0, padding: 0, textAlign: 'left', cursor: 'pointer', font: 'var(--type-meta)', color: 'var(--text-link)' }}
        >
          {t("I remembered it. Back to sign in.")}
        </button>
      </form>
    </div>
  );
}

/** Step two: spend the link. */
export function ResetScreen() {
  const { route, go, logout, t, notify } = useApp();
  const [password, setPassword] = React.useState('');
  /*
   * Typed twice here for the same reason as the signup form, and arguably a
   * better one: somebody on this screen has ALREADY been locked out once. A
   * typo now spends the link and locks them out again, and the next thing they
   * would have to do is go back to their mailbox and start over.
   */
  const [confirm, setConfirm] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [done, setDone] = React.useState(false);
  const [dead, setDead] = React.useState(!route.token);

  const submit = async (e) => {
    e.preventDefault();
    if (saving || !route.token || confirm !== password) return;
    setSaving(true);
    try {
      await api.resetPassword(route.token, password);
      setDone(true);
    } catch (err) {
      /*
       * A password the server refuses does NOT spend the link — the API hashes
       * before it claims the token, precisely so this branch can leave somebody
       * on this screen with a working link and another go. Only a dead token
       * takes the screen away.
       */
      if (err && err.code === 'weak_password') {
        notify(t("That is too short. Use at least {min} characters.", { min: (err.payload && err.payload.minLength) || 12 }), 'caution');
      } else if (err && err.code === 'invalid_or_expired') {
        setDead(true);
      } else {
        notify(err.message, 'caution');
      }
    } finally {
      setSaving(false);
    }
  };

  if (done) {
    return (
      <V.EmptyState
        icon="check"
        title={t("Password changed")}
        description={t("Everything that was signed in to this account has been signed out, including anything that was not you. Sign in with the new password.")}
        /*
         * `logout`, not `go('onboarding')` — and the difference was a bug.
         *
         * The reset ended every session on the server, but this browser had no
         * way of knowing: its cached trader was still sitting in memory, so the
         * header went on offering Trades and Inbox, and onboarding's own guard —
         * "you are already set up, you do not belong here" — called `back()` and
         * dropped the person straight back onto this screen. The button looked
         * like it did nothing.
         *
         * `logout` is the honest description of what has happened anyway: forget
         * the account, clear everything personal that is still in memory, and go
         * to the sign-in screen. Its own API call 401s, which it already expects.
         */
        action={<V.Button onClick={() => { void logout(); }}>{t("Sign in")}</V.Button>}
      />
    );
  }

  if (dead) {
    // Unknown, expired, already spent and superseded all land here and look the
    // same, for the same reason the confirmation screen does it.
    return (
      <V.EmptyState
        icon="x"
        title={t("That link has expired")}
        description={t("Password links last an hour and work once. Ask for a fresh one and use the newest email.")}
        action={<V.Button onClick={() => go('forgot')}>{t("Send me a new link")}</V.Button>}
      />
    );
  }

  return (
    <div style={{ maxWidth: '26rem', margin: '0 auto', padding: 'var(--space-6) var(--space-4)' }}>
      <h2 className="cbm-h2">{t("Choose a new password")}</h2>
      <form onSubmit={submit} style={{ display: 'grid', gap: 'var(--space-4)', marginTop: 'var(--space-5)' }}>
        <V.Input
          label={t("New password")} type="password" autoComplete="new-password"
          value={password} onChange={(e) => setPassword(e.target.value)}
          hint={t("At least 12 characters. Length beats punctuation.")}
        />
        <V.Input
          label={t("Password again")} type="password" autoComplete="new-password"
          value={confirm} onChange={(e) => setConfirm(e.target.value)}
          error={confirm && confirm !== password ? t("These two do not match.") : undefined}
        />
        <V.Button type="submit" disabled={saving || password.length < 12 || confirm !== password}>
          {saving ? t("Saving…") : t("Change my password")}
        </V.Button>
      </form>
    </div>
  );
}
